<template>
    <div
        class="ufo--contact-form"
        :class="[contactFormFieldClasses, { 'form-disabled': isFormLocked }]"
        :style="contactFormFieldStyleVars"
    >
        <SectionText v-if="showTextInModule" :section-text="section.text || section.title" />
        <AnimationWrapper>
            <template #animationContent>
                <form @submit="sendEmail" class="ufo--form" name="contact_form_section">
                    <input type="hidden" name="toEmail" :value="user.contactEmail || user.email" />

                    <label>Email</label>
                    <input class="s-mail form-handler" name="senderEmail" type="email" required placeholder="Your email" :disabled="isFormLocked" />

                    <label>Name</label>
                    <input class="s-name form-handler" name="senderName" type="text" required placeholder="Your name" :disabled="isFormLocked" />

                    <label>Message</label>
                    <textarea name="message" type="text" class="form-handler" required placeholder="Write your message here..." :disabled="isFormLocked"></textarea>
                    <div v-if="submitState === 'idle'">
                        <CtaButton :form-submit-button="true" :section="section" :alignment="textAlign" />
                    </div>
                    <div v-else-if="section.cta && section.cta.show" :class="['ufo--cta-btn-wr', textAlign]">
                        <button
                            type="submit"
                            disabled
                            :class="['btn', 'btn--cta', ctaStyles?.[ctaType]?.fillStyle, ctaStyles?.[ctaType]?.shape, ctaStyles?.[ctaType]?.size, ctaType]"
                        >
                            <span class="cta-content">
                                <span class="cta-label">{{ submitButtonLabel }}</span>
                                <Icon v-if="submitState === 'success'" class="cta-icon" icon-name="check" />
                            </span>
                        </button>
                    </div>
                </form>
            </template>
        </AnimationWrapper>
        <div v-if="statusMessage" class="ufo--form-status-message" :class="{ active: statusActive }">{{ statusMessage }}</div>
    </div>
</template>

<script>
import CtaButton from "@/scripts/view/portfolio/sections/_helper/CtaButton.vue";
import AnimationWrapper from "@/scripts/common/components/AnimationWrapper.vue";
import SectionText from "@/scripts/view/portfolio/sections/_helper/GetSectionText.vue";
import {mapGetters} from "vuex";
import axios from "axios";
import Icon from "@/scripts/common/Icon.vue";
import contactFormFieldStyleMixin from "@/scripts/common/mixins/contactFormFieldStyleMixin.js";
import ctaButtonMixin from "@/scripts/common/mixins/ctaButtonMixin.js";

const MINIMUM_SENDING_STATE_MS = 1500;

export default {
    name: "ContactForm",
    mixins: [contactFormFieldStyleMixin, ctaButtonMixin],
    props: ['section'],
    components: {SectionText, AnimationWrapper, CtaButton, Icon},
    data() {
        return {
            statusMessage: "",
            statusActive: false,
            isSending: false,
            submitState: "idle",
            successTimer: null,
        };
    },
    computed: {
        ...mapGetters("design", ["ctaStyles"]),
        ...mapGetters("view", ["viewData"]),
        textAlign() {
            return this.section.text && this.section.text.style && this.section.text.style.textAlign ? "push-to-" + this.section.text.style.textAlign : ""
        },
        user() {
            return this.viewData.user || this.$store.state.user;
        },
        submitButtonLabel() {
            if (this.submitState === "sending") {
                return "Sending...";
            }
            if (this.submitState === "success") {
                return "Message sent";
            }
            return "";
        },
        isFormLocked() {
            return this.isSending || this.submitState === "success";
        },
        hasDisplayableText() {
            return !!(this.section.text?.text || this.section.title?.text);
        },
        hasSeparateTextModule() {
            return this.section.layoutSettings?.modules?.some((module) => module?.type === "TEXT") ?? false;
        },
        showTextInModule() {
            return this.hasDisplayableText
                && this.section.contentSettings?.showText !== false
                && !this.hasSeparateTextModule
        }
    },
    methods: {
        showStatus(message, autoHide = true) {
            this.statusMessage = message;
            this.statusActive = true;
            if (autoHide) {
                setTimeout(() => {
                    this.statusActive = false;
                    this.statusMessage = "";
                }, 2500);
            }
        },
        clearSuccessTimer() {
            if (this.successTimer) {
                clearTimeout(this.successTimer);
                this.successTimer = null;
            }
        },
        isValidEmail(value) {
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        },
        async sendEmail(e) {
            e.preventDefault();
            if (this.isSending) {
                return false;
            }
            const form = e.target;
            const toEmail = form["toEmail"].value.trim();
            const rawName = this.user.name || "";
            const senderName = form["senderName"].value.trim();
            const senderEmail = form["senderEmail"].value.trim();
            const rawMessage = form["message"].value.trim();

            if (!this.isValidEmail(senderEmail)) {
                this.showStatus("Please enter a valid email address.", false);
                form["senderEmail"].focus();
                return false;
            }

            this.clearSuccessTimer();
            this.statusActive = false;
            this.statusMessage = "";
            this.isSending = true;
            this.submitState = "sending";

            const cleanName = rawName.replace(/[\r\n]+/g, ' ').trim();

            // HTML-formatted message (preserves line breaks in Gmail)
            const htmlMessage = `
        <div>
            <p>Dear ${cleanName},</p>
            <p>${rawMessage.replace(/\n/g, '<br>')}</p>
            <p>
                Best regards,<br>
                <strong>${senderName}</strong><br>
                ${senderEmail}
            </p>
        </div>
    `;

            const textMessage = `Dear ${cleanName},\n\n${rawMessage}\n\nBest regards,\n${senderName}\n${senderEmail}`;

            axios.defaults.headers.common['X-CSRF-TOKEN'] = document
                .querySelector('meta[name="csrf-token"]')
                .getAttribute('content');

            try {
                await Promise.all([
                    axios.post('/portfolio/contact', {
                        toEmail,
                        message: htmlMessage,
                        textMessage,
                        senderName,
                        senderEmail,
                        isHtml: true
                    }),
                    new Promise((resolve) => setTimeout(resolve, MINIMUM_SENDING_STATE_MS))
                ]);

                this.isSending = false;
                this.submitState = "success";
                this.clearSuccessTimer();
                this.successTimer = setTimeout(() => {
                    this.submitState = "idle";
                    form.reset();
                    this.successTimer = null;
                }, 2500);
            } catch {
                this.isSending = false;
                this.submitState = "idle";
                this.showStatus("Something went wrong. Please try again.");
            }

            return false;
        }
    },
    beforeUnmount() {
        this.clearSuccessTimer();
    }
}
</script>
